import React, {createContext, ReactNode, useContext, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Board, CustomTopic, Question} from "../javaTypes";
import "./References.css"
import {ManualData, Program} from "first-common/javaTypes";
import {Alert, Button, CloseButton, Modal} from "react-bootstrap";
import { Tooltip } from 'react-tooltip';
import {loadQuestion, TeamQAQuestion} from "./teamQaClient";

type ReferenceData = ManualData & {
    customTopics: Record<string, CustomTopic>
}

export const ReferenceContext = createContext<ReferenceData | undefined>(undefined);

const frontGroup = '(^|\\s|>|\\()';

function TooltipQuestionRenderer(props: {boardSlug: string | null, questionUrl: string}) {
    const { data: question, isLoading } = useQuery<Question>({
        queryKey: [props.questionUrl],
        queryFn: () => fetch(props.questionUrl).then((response) => response.json()),
        staleTime: 24 * 60 * 60 * 1000, // 1 day
    });
    if (isLoading) {
        return <span><i>Loading...</i></span>
    }
    if (!question) {
        return <span><i>Error loading question data</i></span>;
    }
    return <span><b>{props.boardSlug ?? '#'}{question.number}:</b> {question.title}<br/><hr className={"tooltip-hr"}/><small className={"question-sample"} dangerouslySetInnerHTML={{__html: question.content.replaceAll(/(?<!&)#(\d+)/g, props.boardSlug+"$1")}}/></span>
}

function tooltipRender({content, activeAnchor} : {content: string | null, activeAnchor: HTMLElement | null}) {
    if (content) {
        return <span>content</span>
    }
    if (activeAnchor) {
        const questionURL = activeAnchor?.getAttribute("data-question-url");
        if (questionURL) {
            return <TooltipQuestionRenderer questionUrl={questionURL} boardSlug={activeAnchor.getAttribute('data-board-slug')}/>
        }
    }
    return <span><i>Error loading tooltip...</i></span>
}

function hasAnchorAncestor(element : HTMLElement | Element) : boolean {
    if (element === null) {
        return false;
    }
    return element.tagName === "A" || (element.parentElement != null && hasAnchorAncestor(element.parentElement));
}

export default function ReferenceProvider({board, children} : {board: Board, children: ReactNode}) {
    const customTopics = useQuery<CustomTopic[]>({
        queryKey: ["topics", board],
        queryFn: () => fetch(`/boards/${board.season.cmpYear}/${board.season.program}/${board.slug}/topics`).then(r => r.json())
    }).data;
    const manualData = useQuery<ManualData>({
        queryKey: ["cm", board.season.cmpYear, board.season.program],
        queryFn: () => fetch(`/manual/${board.season.cmpYear}/${board.season.program}/data`).then(r => r.json())
    }).data;
    const data = manualData && customTopics ? { ...manualData, customTopics: Object.fromEntries(customTopics.map(x => [x.label, x])) } : undefined

    return <ReferenceContext.Provider value={data}>
        {children}
        <Tooltip id="reference-tooltip" render={tooltipRender}></Tooltip>
    </ReferenceContext.Provider>
}

export function useOptionalApplyReferences(enabled: boolean, content: string, board: Board, references?: ReferenceData) {
    return useMemo(() => enabled ? applyReferences(content, board, references) : content, [enabled, content, board, references]);
}

export function useApplyReferences(content: string, board: Board, references?: ReferenceData) {
    return useMemo(() => applyReferences(content, board, references), [content, board, references]);
}

export function applyReferences(content: string, board: Board, references?: ReferenceData) {
    // TODO make this section in a common helper with a context or hook or something?
    let result = content ?? "";

    // TO avoid a massive regex of every thing |'d, extract the indices of every match in first pass, then do the replacements later
    let foundTerms: any[] = [];
    if (references) {
        // sort them from longest to shortest for nesting priority
        let values = Object.values(references.terms).sort((a,b) => a.term.length - b.term.length).reverse();
        for (let term of values) {
            const matches = result.matchAll(new RegExp(frontGroup + term.baseRegex, "g"));
            for (let match of matches) {
                if (match[0].startsWith(">")) { // handle a match that is immediately after a closing html tag
                    match[0] = match[0].substring(1);
                    match.index++;
                }
                foundTerms.push({full: match[0], g1: match[2], idx: match.index, term: term});
            }
        }
    }

    // Do the replacements
    foundTerms.sort((a,b) => a.idx - b.idx);
    let offset = 0;
    let letLastTermEnd = 0;
    for (let match of foundTerms) {
        if (offset + match.idx < letLastTermEnd) {
            // console.log("Skipping nested/overlapping term: ", match);
            continue;
        }
        let replacement;
        // MUST USE 's for setting the HTML!
        const trimmedMatch = match.full.trim();
        if (match.full.startsWith(" ")) {
            // start the abbrev tag at index 1:
            replacement = ` <span data-tooltip-id="reference-tooltip" data-tooltip-html='${match.term.definition.replaceAll("'", "&#39;").replaceAll(trimmedMatch, "<u>"+trimmedMatch+"</u>")}'><abbr>${match.full.substring(1)}</abbr></span>`;
        } else {
            replacement = `<span data-tooltip-id="reference-tooltip" data-tooltip-html='${match.term.definition.replaceAll("'", "&#39;").replaceAll(trimmedMatch, "<u>"+trimmedMatch+"</u>")}'><abbr>${match.full}</abbr></span>`;
        }
        result = result.substring(0, offset + match.idx) + replacement + result.substring(offset + match.idx + match.full.length);
        letLastTermEnd = offset + match.idx + replacement.length; // use old offset for this - this is where the new string ends.
        offset += (replacement.length - match.full.length);
    }

    const cmUrl = references?.link
    // Rule & QA refs
    // /([ACEGILRTQ§#]|QA|Section )(\d+)(\.[A-Z\d]+)*/g
    let regex = new RegExp(`${frontGroup}([ACEGILRTQ§#]|QA|Section )(\\d+)(\\.[A-Z\\d]+)*`, "g");
    result = result.replace(regex, (match, frontMatter, g1, g2, g3, offset, fullInput) => {
        if (g1 === "QA" || (board.season.program === Program.FTC && g1 === "Q")) {
            // Q won't work for FRC if they keep Q101!
            if (g1 === "QA") {
                match = "Q" + match.substring(frontMatter.length + 2); //remove frontMatter and QA, replace with Q
            } else {
                match = match.substring(frontMatter.length); // remove frontMatter
            }
            const boardUrl = `/boards/${board.season.cmpYear}/${board.season.program}/QA`;
            return `${frontMatter}<span class="board-reference" data-tooltip-id="reference-tooltip" data-question-url="${boardUrl}/qnum/${g2}" data-board-slug="${board.slug}" data-href="${boardUrl}/qa/${g2}">QA${g2}</span>`
        }
        if (g1 === "#") {
            if (offset > 0 && fullInput[offset-1] === '&') return match;
            const boardUrl = `/boards/${board.season.cmpYear}/${board.season.program}/${board.slug}`;
            return `${frontMatter}<span class="board-reference" data-tooltip-id="reference-tooltip" data-question-url="${boardUrl}/qnum/${g2}" data-board-slug="${board.slug}" data-href="${boardUrl}/qa/${g2}">${board.slug}${g2}</span>`;
        }
        if (!references) {
            return match;
        }
        if (g1 === "§" || g1 === "Section ") {
            match = match.substring(frontMatter.length); // remove frontMatter for indexing below
            const text = g1 === "§" ? match.substring(1) : match.substring(match.indexOf(" ") + 1);
            let fallback = text;
            let section = references.sections[text];
            // Lose granularity if section lookup fails
            while (!section && fallback.includes(".")) {
                fallback = fallback.substring(0, fallback.lastIndexOf("."));
                section = references.sections[fallback];
            }
            if (section && section.href) {
                return `${frontMatter}<span data-href="${cmUrl}#${section.href}" data-tooltip-id="reference-tooltip" data-tooltip-html="${section.name}">Section ${text}</span>`
            }
            return `${frontMatter}<span data-href="${cmUrl}" class="bad">Section ${text}</span>`;
        }

        if (g2.trim().length < 3) {
            // reject a rule match that doesn't have 3 digits.
            return match;
        }
        const ruleId = g1.trim() + g2.trim();
        const rule = references.rules[ruleId];
        if (rule) {
            let clazz;
            if (rule.evergreen) {
                clazz = "rule-evergreen";
            } else if (rule.evergreen === false) {
                clazz = board.season.program === Program.FRC ? "rule-season-frc" : "rule-season";
            } else {
                clazz = "rule-nohead"
            }
            match = match.substring(frontMatter.length);
            // TODO when bad but also known headline?
            return `${frontMatter}<span data-href="${cmUrl}#${rule.href}" data-tooltip-id="reference-tooltip" data-tooltip-html="${rule.headline}" class="${clazz} ${!rule.href ? 'bad' : ''}" >${match}</span>`
        }
        match = match.substring(frontMatter.length);
        return `${frontMatter}<span data-href="${cmUrl}" class="bad" data-tooltip-id="reference-tooltip" data-tooltip-html="Could not find matching rule.">${match}</span>`;
    });
    const resultDoc = new DOMParser().parseFromString(result, "text/html");
    for (let nested of resultDoc.querySelectorAll("span[data-href]")) {
        // look through all of the spans that we have added that have data-href
        // These would prefer to be anchor with proper hrefs, but it is not sound to do so if their parent is an anchor
        // because the DOM spec automatically converts <a href="b"><a href="c"></a></a> to <a href="b"></a><a href="c"></a>
        // So, here we loop through to check whether the span's parent is an anchor. If it isn't an anchor, then upgrade the
        // span to an anchor and convert its data-href field to the href.
        // NOTE: we must use data-href because the DOMParser also very helpfully removes plain href attributes from spans.
        if (nested.parentElement === null) {
            continue;
        }
        if (!hasAnchorAncestor(nested)) {
            const replacementAnchor = document.createElement('a');
            replacementAnchor.innerHTML = nested.innerHTML;
            // copy all attributes
            [...nested.attributes].forEach(({name, value}) =>
                replacementAnchor.setAttribute(name, value)
            );
            replacementAnchor.setAttribute('href', replacementAnchor.getAttribute('data-href') ?? '');  // the selector guarantees this can't be null
            replacementAnchor.setAttribute('target', '_blank');
            nested.parentElement.replaceChild(replacementAnchor, nested);
        }
    }
    return resultDoc.documentElement.innerHTML;
}


export function ReferenceInfo({board, collapsed}: {board: Board, collapsed?: boolean}) {
    const references = useContext(ReferenceContext);
    const [show, setShow] = useState(false);
    const [dismissed, setDismissed] = useState(collapsed);
    const example = {marginLeft: '1ch'};
    const Example = ({str, hidden}: {str: string, hidden?:boolean}) => <div className={"d-flex"} hidden={hidden}>
        <code>{str}</code>: <div style={example} dangerouslySetInnerHTML={{__html: applyReferences(str, board, references)}}/>
    </div>
    return <Alert variant={"secondary"} style={{padding: '.4rem', marginTop: '.5ch'}}>
        <div className={"d-flex justify-content-between"}>
            <small hidden={dismissed}>
                References to various other resources are automatically detected. To utilize them, do the following:
                <ol>
                    <li><strong>Rules:</strong> Type as they appear in the Competition Manual: <code>G101</code> or <code>R101</code>. For sub-lists use a <code>.</code> between the rule and list item: <code>G301.B</code></li>
                    <li><strong>Defined Terms:</strong> Type as they appear in the Competition Manual: <code>ROBOT</code> or <code>YELLOW CARD</code>.</li>
                    <li><strong>Competition Manual Sections:</strong> Type <code>Section #.#.#</code> or <code>§#.#.#</code>. Reference as precise a section number as relevant, but not all sections will link to the full depth.</li>
                    {board.config.isPublic || board.season.program !== Program.FRC ? null : <li><strong>Team QA:</strong> Type <code>QA#</code>.</li>}
                    {board.config.isPublic || board.season.program === Program.FRC ? null : <li><strong>Team QA:</strong> Type <code>QA#</code> or <code>Q#</code>.</li>}
                    <li><strong>Other Questions on this Q&A:</strong> Type <code>#n</code> where <code>n</code> is the question number.</li>
                    {board.config.isPublic ? null : <li><strong>Questions on other Volunteer Q&A:</strong> Avoid unless necessary. Use the block quote inside the editor, as others may not have access to the original content.</li>}
                </ol>
                <div>The "Preview" button will show your content with the references rendered.</div>
                <div><em>The reference parsing and the HTML {board.season.program === Program.FRC ? "Game" : "Competition"} Manual make a best-effort attempt to show, link, and render the correct information.
                    The pdf manual from the <a href={board.season.program === Program.FRC ? "https://www.firstinspires.org/resource-library/frc/competition-manual-qa-system" : `https://${board.season.program.toLowerCase()}.game`}>firstinspires.org Season Materials page</a> is the official manual and supersedes the HTML manual in the case of any discrepancy or parsing error.</em></div>
                <Button size={"sm"} variant={"secondary"} onClick={() => setShow(true)}>Show Examples</Button>
            </small>
            <CloseButton hidden={dismissed} onClick={() => setDismissed(true)}/>

            <small hidden={!dismissed}>Expand for information on formatting for reference auto-detection.</small>
            <Button hidden={!dismissed} size={"sm"} variant={"secondary"} onClick={() => setDismissed(false)}>Expand</Button>
        </div>
        <Modal show={show} size={"lg"}>
            <Modal.Header>Reference Detection Examples</Modal.Header>
            <Modal.Body>
                <ul>
                    <li>Evergreen Rules render as green links, with a tooltip of the rule header:
                        <Example str={"G301"}/>
                    </li>
                    <li>Season Rules render as program-color links, with a tooltip of the rule header:
                        <Example str={"G416"}/>
                    </li>
                    <li>Unknown rules render as red links. This may be either a CM parsing error or typo in the rule number.
                        <Example str={"G999"}/>
                    </li>
                    <li>Defined terms render with a dotted underline and a tooltip containing their definition. These must be exact matches - only UPPERCASE-styled words are detected.
                        <Example str={"FIELD"}/>
                    </li>
                    <li>Sections render as a link with the section name.
                        <Example str={"Section 4"}/>
                        <Example str={"Section 6.2"}/>
                        <Example str={"Section 10.6.1"}/>
                        <Example str={"§10.6.1"}/>
                    </li>
                    <li>Referencing another question renders as links styled like the rectangular tags seen in the corner of answered questions:
                        <Example str={"#4"}/>
                        <Example str={"QA0"}/>
                        <Example str={"Q9"} hidden={board.season.program === Program.FRC}/>
                    </li>
                </ul>
            </Modal.Body>
            <Modal.Footer><Button variant={'secondary'} onClick={() => setShow(false)}>Close</Button></Modal.Footer>
        </Modal>
    </Alert>
}
